import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useRef, useState } from "react";

import { DataTable, FilterBar, UpdatedStamp } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

import {
  APP_LAUNCH_ERROR_TITLE,
  APP_LAUNCH_FLOOR_CAPTION,
  APP_LAUNCH_LOSS_CAPTION,
  APP_LAUNCH_STEPS_CAPTION,
  appLaunchEmpty,
  appLaunchSummaryLine,
  appLaunchWindow,
  barShare,
  biggestLossLine,
  count,
  emailLabel,
  LAUNCH_KPIS_CAPTION,
  LAUNCH_KPIS_PENDING_TEXT,
  formatRate,
  isBiggestLoss,
  NO_DESIRED_ACTION_TEXT,
  NO_DESIRED_ACTION_TITLE,
  stepNumber,
  stepRows,
} from "./app-launch-format";
import { useControls, type Controls } from "./controls";
import { FUNNEL_SLUG, parseRange, RANGE_LABELS, RANGES, type Range } from "./onboarding-format";
import {
  getFunnelAppLaunch,
  statsAppLaunchKey,
  statsMessageFor,
  type AppLaunchEmail,
  type AppLaunchKpi,
  type AppLaunchMilestones,
  type AppLaunchOrders,
  type AppLaunchQuery,
  type AppLaunchStep,
  type FunnelAppLaunchStats,
} from "./stats-api";

/** Before any good range has been read (a custom range with no start date on first load). */
const NO_QUERY: AppLaunchQuery = { from: "", to: "" };

// KET-8425: the App launch tab (UX design 2026-10-27). Where new app customers drop out, whether
// they sign up and order, and whether the launch emails bring them back. Every figure and the
// biggest loss are the funnel stats API's (I-4), unsuppressed (DEC-865); this tab formats and words.

/** UX §6 narrow screen: the table scrolls sideways inside its card while # and Step stay pinned. */
const PINNED_INDEX = "sticky left-0 z-10 bg-background";
const PINNED_STEP = "sticky left-12 z-10 bg-background";
const SCROLL_FRAME = "overflow-x-auto [&_table]:min-w-[40rem]";

const RANGE = "range";
const FROM = "from";
const TO = "to";

const RANGE_SPEC: FilterSpec = { name: RANGE, label: "Range", kind: "choice", options: RANGES.map((r) => ({ value: r, label: RANGE_LABELS[r] })) };

const numeric = { meta: { align: "right" as const } };
const noPaging = (length: number) => ({ pageIndex: 0, pageSize: Math.max(length, 1), rowCount: length });

function AppLaunchControls({ controls, range, rangeError }: { controls: Controls; range: Range; rangeError: string | null }) {
  const set = (name: string, value: string | null) => controls.onChange({ ...controls.values, [name]: value });
  return (
    <div className="flex flex-col gap-3">
      <FilterBar layout="segmented" filters={[RANGE_SPEC]} values={{ ...controls.values, [RANGE]: range }} onChange={controls.onChange} />
      {range === "custom" && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            From
            <Input data-nonmutating type="date" value={String(controls.values[FROM] ?? "")} onChange={(e) => set(FROM, e.target.value || null)} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            To
            <Input data-nonmutating type="date" value={String(controls.values[TO] ?? "")} onChange={(e) => set(TO, e.target.value || null)} />
          </label>
          {rangeError && (
            <span role="alert" className="text-sm text-ks-warn">
              {rangeError}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-base font-bold text-ks-ink">{title}</h2>
      {children}
    </section>
  );
}

// Damien's launch KPIs (funnel-stats specs 07 and 08), in the service's order; rates are the service's.
const kpiColumns: ColumnDef<AppLaunchKpi, unknown>[] = [
  { id: "label", header: "KPI", accessorFn: (k) => k.label },
  { id: "people", header: "People", accessorFn: (k) => count(k.people), ...numeric },
  { id: "rate_from_total", header: "Rate from total entering", accessorFn: (k) => formatRate(k.rate_from_total ?? null), ...numeric },
  { id: "rate_from_previous", header: "Rate from previous step", accessorFn: (k) => formatRate(k.rate_from_previous), ...numeric },
];

const milestoneColumns: ColumnDef<AppLaunchMilestones, unknown>[] = [
  { id: "app_entry", header: "App entry", accessorFn: (m) => count(m.app_entry), ...numeric },
  { id: "onboarding_started", header: "Onboarding started", accessorFn: (m) => count(m.onboarding_started), ...numeric },
  { id: "step_viewed", header: "Step viewed", accessorFn: (m) => count(m.step_viewed), ...numeric },
  { id: "sign_up_started", header: "Sign-up started", accessorFn: (m) => count(m.sign_up_started), ...numeric },
  { id: "sign_up", header: "Signed up", accessorFn: (m) => count(m.sign_up), ...numeric },
  { id: "onboarding_complete", header: "Onboarding complete", accessorFn: (m) => count(m.onboarding_complete), ...numeric },
];

const orderColumns: ColumnDef<AppLaunchOrders, unknown>[] = [
  { id: "dose_plan_created", header: "Dose plan created", accessorFn: (o) => count(o.dose_plan_created), ...numeric },
  { id: "product_checked", header: "Product checked", accessorFn: (o) => count(o.product_checked), ...numeric },
  { id: "checkout_opened", header: "Checkout opened", accessorFn: (o) => count(o.checkout_opened), ...numeric },
  { id: "checkout_claimed", header: "Checkout claimed", accessorFn: (o) => count(o.checkout_claimed), ...numeric },
  { id: "order_placed", header: "Order placed", accessorFn: (o) => count(o.order_placed), ...numeric },
  { id: "order_rate", header: "Order rate", accessorFn: (o) => formatRate(o.order_rate), ...numeric },
  { id: "orders_placed", header: "Orders", accessorFn: (o) => count(o.orders_placed), ...numeric },
];

/** Purple only on the row the service named the biggest loss; every other bar is grey. */
function LossCell({ row, steps, biggest }: { row: AppLaunchStep; steps: AppLaunchStep[]; biggest: boolean }) {
  const share = barShare(row, steps);
  return (
    <span className="flex items-center justify-end gap-2">
      <span>{formatRate(row.loss_rate)}</span>
      <span aria-hidden="true" className="inline-block h-1.5 w-16 shrink-0">
        {share > 0 && <span className={`block h-full ${biggest ? "bg-ks-purple" : "bg-ks-grey-2"}`} style={{ width: `${share}%` }} />}
      </span>
    </span>
  );
}

function stepColumns(steps: AppLaunchStep[], biggestLoss: FunnelAppLaunchStats["biggest_loss"]): ColumnDef<AppLaunchStep, unknown>[] {
  return [
    { id: "index", header: "#", accessorFn: (s) => stepNumber(s), meta: { align: "right", width: "3rem", className: PINNED_INDEX } },
    {
      id: "step",
      header: "Step",
      accessorFn: (s) => s.step_id,
      cell: ({ row }) => (
        <span className={`flex items-center gap-2 ${row.original.people_reached === 0 ? "opacity-50" : ""}`}>
          {row.original.label && <span className="text-ks-ink">{row.original.label}</span>}
          <code className={row.original.label ? "text-xs text-muted-foreground" : "text-ks-ink"}>{row.original.step_id}</code>
          {isBiggestLoss(row.original, biggestLoss) && <span className="text-xs font-bold text-ks-purple">Biggest loss</span>}
        </span>
      ),
      meta: { width: "20%", className: PINNED_STEP },
    },
    { id: "flow", header: "Flow / stage", accessorFn: (s) => `${s.flow} · ${s.stage}`, meta: { width: "20%" } },
    { id: "reached", header: "Reached", accessorFn: (s) => count(s.people_reached), meta: { align: "right", width: "10%" } },
    { id: "completed", header: "Completed", accessorFn: (s) => count(s.people_completed), meta: { align: "right", width: "10%" } },
    { id: "in_progress", header: "In progress", accessorFn: (s) => count(s.people_in_progress), meta: { align: "right", width: "10%" } },
    { id: "lost", header: "Lost", accessorFn: (s) => count(s.people_lost), meta: { align: "right", width: "10%" } },
    {
      id: "loss_rate",
      header: "Loss rate",
      accessorFn: (s) => formatRate(s.loss_rate),
      cell: ({ row }) => <LossCell row={row.original} steps={steps} biggest={isBiggestLoss(row.original, biggestLoss)} />,
      meta: { align: "right", width: "20%", sortable: true },
    },
  ];
}

const emailColumns: ColumnDef<AppLaunchEmail, unknown>[] = [
  { id: "email", header: "Email", accessorFn: (e) => emailLabel(e) },
  { id: "sent", header: "Sent", accessorFn: (e) => count(e.sent), ...numeric },
  { id: "delivered", header: "Delivered", accessorFn: (e) => count(e.delivered), ...numeric },
  { id: "open_rate", header: "Open rate", accessorFn: (e) => formatRate(e.open_rate), ...numeric },
  { id: "click_rate", header: "Click rate", accessorFn: (e) => formatRate(e.click_rate), ...numeric },
  { id: "returned", header: "Returned to app", accessorFn: (e) => count(e.returned_to_app), ...numeric },
  {
    id: "desired",
    header: "Desired action",
    accessorFn: (e) => (e.has_desired_action ? formatRate(e.desired_action_rate) : NO_DESIRED_ACTION_TEXT),
    cell: ({ row }) =>
      !row.original.has_desired_action ? (
        <span title={NO_DESIRED_ACTION_TITLE}>{NO_DESIRED_ACTION_TEXT}</span>
      ) : (
        formatRate(row.original.desired_action_rate)
      ),
    ...numeric,
  },
];

export function AppLaunchTab({ areaName }: { areaName: string }) {
  const controls = useControls();
  const [sort, setSort] = useState<"asc" | "desc" | null>(null);
  const range = parseRange(controls.values[RANGE]);
  const window = appLaunchWindow(range, new Date(), { from: controls.values[FROM], to: controls.values[TO] });
  const rangeError = "error" in window ? window.error : null;
  // A bad custom range keeps the last good request on screen beside its error.
  const lastGood = useRef<AppLaunchQuery | null>(null);
  if ("from" in window) lastGood.current = { from: window.from, to: window.to };
  const query = lastGood.current ?? NO_QUERY;
  // No placeholder data: a new range shows the skeleton, never the previous request's figures under the new label.
  const appLaunch = useQuery({
    queryKey: statsAppLaunchKey(FUNNEL_SLUG, query),
    queryFn: () => getFunnelAppLaunch(FUNNEL_SLUG, query),
    enabled: query !== NO_QUERY,
  });
  const stats = appLaunch.data?.data;
  const empty = stats ? appLaunchEmpty(stats) : null;
  const rows = stats ? stepRows(stats.step_loss, sort) : [];
  const loading = appLaunch.isLoading;

  return (
    <div className="flex flex-col gap-4">
      <AppLaunchControls controls={controls} range={range} rangeError={rangeError} />
      {appLaunch.isError ? (
        <Alert role="alert">
          <AlertTitle>{APP_LAUNCH_ERROR_TITLE}</AlertTitle>
          <AlertDescription>
            {statsMessageFor(appLaunch.error, areaName, "App launch figures")}{" "}
            <Button data-nonmutating variant="link" className="px-0" onClick={() => void appLaunch.refetch()}>
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      ) : empty ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <>
          {stats ? (
            <div className="flex flex-col gap-1">
              <p className="text-base text-ks-ink" aria-live="polite">
                {appLaunchSummaryLine(stats, range, query)}
              </p>
              <p className="text-base font-bold text-ks-ink">{biggestLossLine(stats)}</p>
              <UpdatedStamp generatedAt={appLaunch.data?.meta.data_as_of ?? null} />
            </div>
          ) : (
            loading && <Skeleton className="h-12 w-full" />
          )}
          <Section title="Launch KPIs">
            {stats && !stats.launch_kpis ? (
              <p className="text-sm text-muted-foreground">{LAUNCH_KPIS_PENDING_TEXT}</p>
            ) : (
              <>
                <div className={SCROLL_FRAME}>
                  <DataTable
                    columns={kpiColumns}
                    rows={stats?.launch_kpis ?? []}
                    pending={loading}
                    density="compact"
                    page={noPaging(stats?.launch_kpis?.length ?? 0)}
                    onPageChange={() => {}}
                    emptyMessage="No launch KPIs to show."
                    caption="Launch KPIs in funnel order: Entered onboarding, Results Given, Account created, Order Made, Precision Home reached"
                  />
                </div>
                <p className="text-sm text-muted-foreground">{LAUNCH_KPIS_CAPTION}</p>
              </>
            )}
          </Section>
          <Section title="Milestones">
            <div className={SCROLL_FRAME}>
              <DataTable
                columns={milestoneColumns}
                rows={stats ? [stats.milestones] : []}
                pending={loading}
                density="compact"
                page={noPaging(1)}
                onPageChange={() => {}}
                emptyMessage="No milestones to show."
                caption="Launch milestones: people who reached each point"
              />
            </div>
          </Section>
          <Section title="Step loss by screen">
            <div className={SCROLL_FRAME}>
              <DataTable
                columns={stepColumns(rows, stats?.biggest_loss ?? null)}
                rows={rows}
                pending={loading}
                density="compact"
                page={noPaging(rows.length)}
                onPageChange={() => {}}
                sort={sort ? { id: "loss_rate", direction: sort } : undefined}
                onSort={() => setSort((s) => (s === "desc" ? "asc" : s === "asc" ? null : "desc"))}
                emptyMessage="No app launch steps to show."
                caption="App launch steps with people reached, completed, in progress, lost and loss rate"
              />
            </div>
            <p className="text-sm text-muted-foreground">{APP_LAUNCH_STEPS_CAPTION}</p>
            <p className="text-sm text-muted-foreground">{APP_LAUNCH_LOSS_CAPTION}</p>
            <p className="text-sm text-muted-foreground">{APP_LAUNCH_FLOOR_CAPTION}</p>
          </Section>
          <Section title="App orders">
            <div className={SCROLL_FRAME}>
              <DataTable
                columns={orderColumns}
                rows={stats ? [stats.orders] : []}
                pending={loading}
                density="compact"
                page={noPaging(1)}
                onPageChange={() => {}}
                emptyMessage="No orders to show."
                caption="App orders: people at each step from dose plan to order placed"
              />
            </div>
          </Section>
          <Section title="Email response">
            <div className={SCROLL_FRAME}>
              <DataTable
                columns={emailColumns}
                rows={stats?.email ?? []}
                pending={loading}
                density="compact"
                page={noPaging(stats?.email.length ?? 0)}
                onPageChange={() => {}}
                emptyMessage="No launch emails sent in this window yet."
                caption="Launch emails with sent, delivered, open rate, click rate, returned to app and desired action"
              />
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
